import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllShops, getNearbyShops } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const SERVICE_LABELS = {
  washing: '🫧 Washing',
  dry_cleaning: '👔 Dry Cleaning',
  laundry: '🧺 Laundry',
  ironing: '♨️ Ironing',
};

function StarRating({ rating, size = 'sm' }) {
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} style={{ color: n <= Math.round(rating) ? '#f59e0b' : '#d1d5db', fontSize: size === 'sm' ? '0.9rem' : '1.1rem' }}>★</span>
      ))}
      {rating > 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 4 }}>{rating.toFixed(1)}</span>}
    </div>
  );
}

function ShopCard({ shop, onSelect }) {
  return (
    <div className="card shop-card" onClick={() => onSelect(shop)}>
      <div className="shop-card-header">
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, background: 'var(--primary-light)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>🏪</div>
            <div className="shop-name">{shop.name}</div>
          </div>
          <div className="shop-address">📍 {shop.address}</div>
        </div>
        <div>
          {shop.avg_rating > 0
            ? <div className="shop-rating">★ {shop.avg_rating.toFixed(1)} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({shop.total_ratings})</span></div>
            : <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>No ratings yet</span>
          }
        </div>
      </div>
      <div className="shop-card-body">
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Services</div>
          <div className="shop-services">
            {shop.services?.map(s => (
              <span key={s} className="order-service-tag">{SERVICE_LABELS[s] || s}</span>
            ))}
          </div>
        </div>
        {shop.description && (
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>{shop.description}</p>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
            onClick={e => { e.stopPropagation(); onSelect(shop, true); }}>
            📦 Order from here
          </button>
          <button className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); onSelect(shop); }}>
            View
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShopsPage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [selectedShop, setSelectedShop] = useState(null);
  const [nearbyMode, setNearbyMode] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      const res = await getAllShops();
      setShops(res.data);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const loadNearby = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const res = await getNearbyShops(pos.coords.latitude, pos.coords.longitude);
        setShops(res.data);
        setNearbyMode(true);
      } catch (e) { }
      finally { setLoading(false); }
    }, () => { setLoading(false); alert('Location access denied'); });
  };

  const handleSelect = (shop, directOrder = false) => {
    if (directOrder) {
      if (!user) { navigate('/login'); return; }
      navigate(`/customer/new-order?shop=${shop.id}`);
    } else {
      setSelectedShop(shop);
    }
  };

  const filtered = shops.filter(s => {
    const matchSearch = s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.address?.toLowerCase().includes(search.toLowerCase());
    const matchService = filterService === 'all' || s.services?.includes(filterService);
    return matchSearch && matchService;
  });

  // Shop detail modal
  if (selectedShop) {
    return (
      <div className="page" style={{ maxWidth: 600 }}>
        <button className="btn btn-ghost" onClick={() => setSelectedShop(null)} style={{ marginBottom: 16 }}>
          ← Back to Shops
        </button>
        <div className="card">
          <div style={{ padding: '2rem', borderBottom: '1px solid var(--border)', background: 'linear-gradient(135deg, var(--primary-light), white)', borderRadius: '16px 16px 0 0' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ width: 64, height: 64, background: 'white', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', boxShadow: 'var(--shadow)', flexShrink: 0 }}>🏪</div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '1.4rem', marginBottom: 4 }}>{selectedShop.name}</h2>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 6 }}>📍 {selectedShop.address}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 8 }}>📞 {selectedShop.phone}</div>
                {selectedShop.avg_rating > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StarRating rating={selectedShop.avg_rating} size="md" />
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      {selectedShop.total_ratings} review{selectedShop.total_ratings !== 1 ? 's' : ''}
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>No reviews yet</span>
                )}
              </div>
            </div>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {selectedShop.description && (
              <p style={{ color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>{selectedShop.description}</p>
            )}
            <div style={{ marginBottom: 20 }}>
              <div className="form-label">Services</div>
              <div className="service-options">
                {selectedShop.services?.map(s => (
                  <div key={s} className="service-chip selected" style={{ cursor: 'default' }}>
                    {SERVICE_LABELS[s] || s}
                  </div>
                ))}
              </div>
            </div>
            {user?.role === 'customer' ? (
              <button className="btn btn-primary btn-full btn-lg"
                onClick={() => navigate(`/customer/new-order?shop=${selectedShop.id}`)}>
                📦 Order from {selectedShop.name}
              </button>
            ) : (
              <button className="btn btn-primary btn-full btn-lg" onClick={() => navigate('/login')}>
                Login to Order →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Laundry Shops</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
            {nearbyMode ? '📍 Showing nearby shops' : `${shops.length} shops available`}
          </p>
        </div>
        <button className="btn btn-outline" onClick={loadNearby}>
          📍 Near Me
        </button>
      </div>

      {/* Search & filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input className="form-control" placeholder="🔍 Search shops..."
          style={{ flex: 1, minWidth: 200 }}
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-control" style={{ width: 'auto' }}
          value={filterService} onChange={e => setFilterService(e.target.value)}>
          <option value="all">All Services</option>
          <option value="washing">Washing</option>
          <option value="dry_cleaning">Dry Cleaning</option>
          <option value="laundry">Laundry</option>
          <option value="ironing">Ironing</option>
        </select>
      </div>

      {loading && <div className="spinner" />}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔍</div>
          <p>No shops found matching your search.</p>
        </div>
      )}

      <div className="grid-auto">
        {filtered.map(shop => (
          <ShopCard key={shop.id} shop={shop} onSelect={handleSelect} />
        ))}
      </div>

      {/* CTA for owners */}
      {!loading && (
        <div style={{ marginTop: '3rem', textAlign: 'center', padding: '2rem', border: '2px dashed var(--border)', borderRadius: 16 }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏪</div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>Own a laundry shop? Get orders from customers nearby.</p>
          <button className="btn btn-primary" onClick={() => navigate('/login')}>Register Your Shop →</button>
        </div>
      )}
    </div>
  );
}
